import { datadogRum } from "@datadog/browser-rum";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logError } from "@src/appV2/lib/analytics/log";
import { Platform } from "@src/mobile/utils/platform";
// eslint-disable-next-line no-restricted-imports -- This is the lock wrapper; it must import the raw function.
import { fetchAuthSession as amplifyFetchAuthSession } from "aws-amplify/auth";

import { readCapacitorCognitoSession } from "./capacitorCognitoSession";

const LOCK_NAME = "cognito-token-refresh";

/**
 * Threshold after which a `fetchAuthSessionWithLock` call is considered hung
 * for diagnostic purposes. Successful refreshes complete in well under one
 * second; values above this point at a stuck Web Locks acquisition or a
 * wedged Amplify `fetchAuthSession`.
 *
 * Observational only — we never reject the underlying promise. The goal is
 * to surface signal in Datadog the next time the silent hang occurs.
 */
const HANG_DETECTION_MS = 8000;

type LockPhase = "awaiting_lock" | "awaiting_amplify" | "post_amplify";

/**
 * Cross-tab lock for Cognito token refresh using the Web Locks API.
 *
 * AWS Cognito uses refresh-token rotation: once a refresh token is consumed,
 * the old one is invalidated. Amplify v6 already deduplicates concurrent
 * `fetchAuthSession` calls **within a single tab** via `deDupeAsyncFunction`.
 * However, when multiple browser tabs trigger a refresh simultaneously, each
 * tab sends the same refresh token independently — the second tab receives a
 * `RefreshTokenReuseException`, which clears tokens and signs the user out.
 *
 * This module wraps `fetchAuthSession` with `navigator.locks.request` so that
 * only one tab can refresh at a time. While one tab holds the lock and
 * refreshes tokens, other tabs wait. When the waiting tab acquires the lock
 * the tokens in `localStorage` are already fresh, so Amplify reads them
 * without issuing another network refresh.
 *
 * Falls back to calling `fetchAuthSession` directly when the Web Locks API
 * is not available (e.g., non-secure contexts).
 *
 * On Capacitor, this delegates to `readCapacitorCognitoSession` which
 * bypasses Amplify entirely. See that file for the rationale.
 */

type AuthSessionResult = Awaited<ReturnType<typeof amplifyFetchAuthSession>>;
type FetchAuthSessionOptions = Parameters<typeof amplifyFetchAuthSession>[0];

function generateCorrelationId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Drop-in replacement for Amplify's `fetchAuthSession` that coordinates
 * token refreshes across browser tabs via the Web Locks API.
 *
 * Within a single tab, Amplify's built-in `deDupeAsyncFunction` already
 * prevents duplicate refresh requests. This wrapper adds the cross-tab
 * guarantee: only one tab holds the lock at a time, so concurrent tabs
 * never race with the same refresh token.
 *
 * On Capacitor, returns a shape-compatible session read directly from
 * localStorage (see `capacitorCognitoSession.ts`).
 *
 * Emits phase events at start, lock acquisition, Amplify return, and
 * completion, plus a `HANG_DETECTED` action when any phase exceeds
 * `HANG_DETECTION_MS`. A silent hang in `navigator.locks.request` or
 * Amplify's `fetchAuthSession` shows up in Datadog as a stalled phase.
 */
export async function fetchAuthSessionWithLock(
  options?: FetchAuthSessionOptions
): Promise<AuthSessionResult> {
  if (Platform.isMobile) {
    // The cast is safe: `CapacitorAuthSession` is a structural subset of
    // `AuthSessionResult` that contains all the fields callers actually use
    // (`tokens.idToken.payload`, `tokens.idToken.toString()`,
    // `tokens.accessToken`, `tokens.refreshToken`). Capacitor never has
    // `credentials`/`identityId`/`userSub` because we don't use Amplify's
    // identity-pool flow there.
    return (await readCapacitorCognitoSession(options)) as AuthSessionResult;
  }

  const correlationId = generateCorrelationId();
  const startMs = Date.now();
  const forceRefresh = options?.forceRefresh ?? false;
  const hasLocks = typeof navigator !== "undefined" && "locks" in navigator;
  let phase: LockPhase = "awaiting_lock";

  const logContext = {
    correlationId,
    forceRefresh,
    hasLocks,
  };

  datadogRum.addAction(APP_V2_APP_EVENTS.COGNITO_SESSION_LOCK_STARTED, logContext);

  let hangFired = false;
  const hangTimer = setTimeout(() => {
    hangFired = true;
    datadogRum.addAction(APP_V2_APP_EVENTS.COGNITO_SESSION_LOCK_HANG_DETECTED, {
      ...logContext,
      phase,
      elapsedMs: Date.now() - startMs,
      thresholdMs: HANG_DETECTION_MS,
    });
  }, HANG_DETECTION_MS);

  try {
    const result = await runFetchAuthSession(options, hasLocks, () => {
      phase = "awaiting_amplify";
      datadogRum.addAction(APP_V2_APP_EVENTS.COGNITO_SESSION_LOCK_ACQUIRED, {
        ...logContext,
        acquireMs: Date.now() - startMs,
      });
    });

    phase = "post_amplify";
    datadogRum.addAction(APP_V2_APP_EVENTS.COGNITO_SESSION_AMPLIFY_RETURNED, {
      ...logContext,
      amplifyMs: Date.now() - startMs,
    });

    return result;
  } catch (error) {
    logError(APP_V2_APP_EVENTS.COGNITO_SESSION_LOCK_FAILURE, {
      error,
      metadata: {
        ...logContext,
        phase,
        elapsedMs: Date.now() - startMs,
      },
    });
    throw error;
  } finally {
    clearTimeout(hangTimer);
    datadogRum.addAction(APP_V2_APP_EVENTS.COGNITO_SESSION_LOCK_COMPLETED, {
      ...logContext,
      totalMs: Date.now() - startMs,
      hangFired,
    });
  }
}

async function runFetchAuthSession(
  options: FetchAuthSessionOptions | undefined,
  hasLocks: boolean,
  onAcquired: () => void
): Promise<AuthSessionResult> {
  if (!hasLocks) {
    onAcquired();
    return amplifyFetchAuthSession(options);
  }

  return navigator.locks.request(LOCK_NAME, async () => {
    onAcquired();
    return amplifyFetchAuthSession(options);
  });
}
